import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import Select, { ValueType } from 'react-select';
import moment from 'moment';

import Button, {
  ButtonSize,
  ButtonType
} from '../../../shared/components/core-ui/button/button.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { Pagination } from '../../../shared/components/core-ui/pagination/pagination.component';
import { isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { APP_ROUTES } from '../../../shared/config/routes-config';
import { RouteConfig } from '../../../shared/interfaces/routes-config.interface';
import {
  OverduePaymentRowModel,
  OverduePaymentsFilterOptionsModel,
  OverduePaymentsQueryModel,
  OverduePaymentsSearchModel,
  OverduePaymentsSortBy,
  OverduePaymentsTotalsModel
} from '../../../shared/models/overdue-payments.model';
import { triggerBrowserDownload } from '../../../shared/services/utils/download.util';
import { LoadingData } from '../../../ui/LoadingData';
import { NotFoundData } from '../../../ui/NotFoundData';
import {
  exportOverduePayments,
  getOverduePayments,
  getOverduePaymentsFilterOptions
} from '../../shared/services/overdue-payments.service';

const selectMenuPortalStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
};

const EMPTY_OPTIONS: OverduePaymentsFilterOptionsModel = {
  paymentStatuses: [],
  reservationStatuses: [],
  cities: [],
  areas: [],
  properties: []
};

const EMPTY_TOTALS: OverduePaymentsTotalsModel = {
  payments: 0,
  reservations: 0,
  remainingEgp: 0,
  remainingUsd: 0,
  remainingEgpEquivalent: 0
};

const SORT_OPTIONS: { value: OverduePaymentsSortBy; label: string }[] = [
  { value: 'dueDate', label: 'Oldest due first' },
  { value: 'remaining', label: 'Largest outstanding first' },
  { value: 'checkIn', label: 'Most recent check-in' },
  { value: 'created', label: 'Most recently booked' }
];

/** Human labels for the two statuses the report covers. The API sends the raw values. */
const PAYMENT_STATUS_LABELS: { [key: string]: string } = {
  overdue: 'Overdue',
  partially_collected: 'Partially collected'
};

type StringOption = { value: string; label: string };

const formatMoney = (value?: number) => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatDate = (ms?: number) => {
  if (!ms) return '—';
  return moment(ms).format('DD MMM YYYY');
};

const titleCase = (value: string) =>
  (value || '').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

/** Reads a react-select multi value as a plain array of ids. */
const toIds = (value: ValueType<StringOption, true>): string[] =>
  ((value as StringOption[]) || []).map((option) => option.value);

const COLUMNS = [
  'Payment',
  'Status',
  'Due',
  'Days late',
  'Total',
  'Collected',
  'Outstanding',
  'Outstanding (EGP equiv.)',
  'Reservation',
  'Reservation status',
  'Booked',
  'Check-in',
  'Check-out',
  'Guest',
  'Phone',
  'Email',
  'City',
  'Area',
  'Project',
  'Unit',
  'Collector'
];

const OverduePaymentsContainer = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [rows, setRows] = useState<OverduePaymentRowModel[]>([]);
  const [count, setCount] = useState(0);
  const [totals, setTotals] = useState<OverduePaymentsTotalsModel>(EMPTY_TOTALS);
  const [truncated, setTruncated] = useState(false);

  const [offset, setOffset] = useState(1);
  const [limit, setLimit] = useState(20);

  const [sortBy, setSortBy] = useState<OverduePaymentsSortBy>('dueDate');

  const [paymentStatuses, setPaymentStatuses] = useState<string[]>([]);
  const [reservationStatuses, setReservationStatuses] = useState<string[]>([]);
  const [cityIds, setCityIds] = useState<string[]>([]);
  const [areaIds, setAreaIds] = useState<string[]>([]);
  const [propertyIds, setPropertyIds] = useState<string[]>([]);

  const [createdFrom, setCreatedFrom] = useState<Date | null>(null);
  const [createdTo, setCreatedTo] = useState<Date | null>(null);
  const [checkInFrom, setCheckInFrom] = useState<Date | null>(null);
  const [checkInTo, setCheckInTo] = useState<Date | null>(null);
  const [checkOutFrom, setCheckOutFrom] = useState<Date | null>(null);
  const [checkOutTo, setCheckOutTo] = useState<Date | null>(null);
  const [dueDateFrom, setDueDateFrom] = useState<Date | null>(null);
  const [dueDateTo, setDueDateTo] = useState<Date | null>(null);

  const [options, setOptions] = useState<OverduePaymentsFilterOptionsModel>(EMPTY_OPTIONS);

  /**
   * The filters that produced the report currently on screen.
   *
   * Held apart from the live filter state so that paging, re-sorting and exporting act on the
   * report the operator is looking at. Rebuilding the query from the live state instead would mean
   * editing a dropdown and then clicking page 2 silently returns page 2 of a *different* result —
   * with different totals — while the pager still shows the old page count.
   */
  const [appliedQuery, setAppliedQuery] = useState<OverduePaymentsQueryModel | null>(null);

  /**
   * Sequence number of the newest in-flight report request.
   *
   * Two requests can easily overlap — hit Generate on an unfiltered run, then change rows per page
   * — and without this the slower one wins, leaving one query's rows under another query's
   * pagination and totals.
   */
  const requestSequence = useRef(0);

  useEffect(() => {
    getOverduePaymentsFilterOptions()
      .then((response) => {
        if (!isErrorInfo(response)) {
          // Merged over the defaults rather than replacing them: every dropdown below maps over
          // these arrays unguarded, so a response missing one of them would throw on the next
          // render and blank the whole page instead of showing one empty dropdown.
          setOptions({ ...EMPTY_OPTIONS, ...response });
        } else {
          birdToast.error('Filters could not be loaded', 'Overdue payments');
        }
      })
      .catch(() => birdToast.error('Filters could not be loaded', 'Overdue payments'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // City narrows areas, area narrows projects. Selecting a city and then an area inside it narrows
  // to that area — the server intersects the two rather than widening back to the city.
  const cityOptions = useMemo(
    () => options.cities.map((city) => ({ value: city.id, label: city.name })),
    [options.cities]
  );

  const areasInScope = useMemo(
    () => options.areas.filter((area) => cityIds.length === 0 || cityIds.includes(area.cityId)),
    [cityIds, options.areas]
  );

  const areaOptions = useMemo(
    () => areasInScope.map((area) => ({ value: area.id, label: area.name })),
    [areasInScope]
  );

  const propertyOptions = useMemo(() => {
    // With no city or area chosen, every project is offered — filtering by the full area list
    // instead would quietly hide any project whose areaId is blank.
    if (cityIds.length === 0 && areaIds.length === 0) {
      return options.properties.map((property) => ({
        value: property.id,
        label: property.name
      }));
    }

    const scopedAreaIds = areaIds.length > 0 ? areaIds : areasInScope.map((area) => area.id);

    return options.properties
      .filter((property) => scopedAreaIds.includes(property.areaId))
      .map((property) => ({ value: property.id, label: property.name }));
  }, [areaIds, areasInScope, cityIds, options.properties]);

  const paymentStatusOptions = useMemo(
    () =>
      options.paymentStatuses.map((status) => ({
        value: status.id,
        label: PAYMENT_STATUS_LABELS[status.id] || titleCase(status.name)
      })),
    [options.paymentStatuses]
  );

  const reservationStatusOptions = useMemo(
    () =>
      options.reservationStatuses.map((status) => ({
        value: status.id,
        label: titleCase(status.name)
      })),
    [options.reservationStatuses]
  );

  /**
   * The filters as the API wants them.
   *
   * Both ends of every range are inclusive, so a "to" date is sent as the last instant of that
   * day. Sending its midnight instead would drop everything that happened during the day the
   * operator picked, which is the kind of off-by-one nobody notices until a total is short.
   */
  const buildQuery = useCallback((): OverduePaymentsQueryModel => {
    return {
      ...(paymentStatuses.length > 0 ? { paymentStatuses } : {}),
      ...(reservationStatuses.length > 0 ? { reservationStatuses } : {}),
      ...(cityIds.length > 0 ? { cityIds } : {}),
      ...(areaIds.length > 0 ? { areaIds } : {}),
      ...(propertyIds.length > 0 ? { propertyIds } : {}),
      ...(createdFrom ? { createdFrom: moment(createdFrom).startOf('day').valueOf() } : {}),
      ...(createdTo ? { createdTo: moment(createdTo).endOf('day').valueOf() } : {}),
      ...(checkInFrom ? { checkInFrom: moment(checkInFrom).startOf('day').valueOf() } : {}),
      ...(checkInTo ? { checkInTo: moment(checkInTo).endOf('day').valueOf() } : {}),
      ...(checkOutFrom ? { checkOutFrom: moment(checkOutFrom).startOf('day').valueOf() } : {}),
      ...(checkOutTo ? { checkOutTo: moment(checkOutTo).endOf('day').valueOf() } : {}),
      ...(dueDateFrom ? { dueDateFrom: moment(dueDateFrom).startOf('day').valueOf() } : {}),
      ...(dueDateTo ? { dueDateTo: moment(dueDateTo).endOf('day').valueOf() } : {})
    };
  }, [
    areaIds,
    checkInFrom,
    checkInTo,
    checkOutFrom,
    checkOutTo,
    cityIds,
    createdFrom,
    createdTo,
    dueDateFrom,
    dueDateTo,
    paymentStatuses,
    propertyIds,
    reservationStatuses
  ]);

  const loadPayments = useCallback(
    (
      page: number,
      perPage: number,
      sort: OverduePaymentsSortBy,
      query: OverduePaymentsQueryModel
    ) => {
      const sequence = requestSequence.current + 1;
      requestSequence.current = sequence;

      setLoading(true);
      setError(false);

      const search: OverduePaymentsSearchModel = {
        offset: page,
        limit: perPage,
        sortBy: sort,
        sortDirection: sort === 'dueDate' ? 'asc' : 'desc',
        query
      };

      /** Ignore a response that a newer request has already superseded. */
      const isCurrent = () => requestSequence.current === sequence;

      const clear = () => {
        setRows([]);
        setCount(0);
        setTotals(EMPTY_TOTALS);
        setTruncated(false);
      };

      getOverduePayments(search)
        .then((response) => {
          if (!isCurrent()) {
            return;
          }

          setLoading(false);
          setHasSearched(true);

          if (isErrorInfo(response)) {
            setError(true);
            clear();
            return;
          }

          setRows(response.rows);
          setCount(response.count);
          setTotals(response.totals);
          setTruncated(response.truncated);
        })
        // apisauce resolves on an HTTP error, so this only fires on a transport failure or a throw
        // in the handler above. Without it the spinner would never come down and the operator
        // would have to reload the page.
        .catch(() => {
          if (!isCurrent()) {
            return;
          }

          setLoading(false);
          setHasSearched(true);
          setError(true);
          clear();
        });
    },
    []
  );

  /** Date ranges only make sense one way round; a reversed pair returns nothing, silently. */
  const validateRanges = (): boolean => {
    const pairs: [Date | null, Date | null, string][] = [
      [createdFrom, createdTo, 'Booked'],
      [checkInFrom, checkInTo, 'Check-in'],
      [checkOutFrom, checkOutTo, 'Check-out'],
      [dueDateFrom, dueDateTo, 'Due date']
    ];

    const reversed = pairs.find(
      ([from, to]) => from && to && moment(to).isBefore(moment(from), 'day')
    );

    if (reversed) {
      birdToast.error(`${reversed[2]} "to" date is before its "from" date`, 'Overdue payments');
      return false;
    }

    return true;
  };

  const handleGenerate = () => {
    if (!validateRanges()) {
      return;
    }
    const query = buildQuery();
    setAppliedQuery(query);
    setOffset(1);
    loadPayments(1, limit, sortBy, query);
  };

  const handleSortChange = (next: OverduePaymentsSortBy) => {
    if (next === sortBy) {
      return;
    }
    setSortBy(next);
    setOffset(1);
    if (appliedQuery) {
      loadPayments(1, limit, next, appliedQuery);
    }
  };

  const handleClearFilters = () => {
    setPaymentStatuses([]);
    setReservationStatuses([]);
    setCityIds([]);
    setAreaIds([]);
    setPropertyIds([]);
    setCreatedFrom(null);
    setCreatedTo(null);
    setCheckInFrom(null);
    setCheckInTo(null);
    setCheckOutFrom(null);
    setCheckOutTo(null);
    setDueDateFrom(null);
    setDueDateTo(null);
  };

  /**
   * Exports every payment behind the report on screen, not the visible page — the page is a
   * preview of the list, and the list is what gets worked through.
   *
   * Exports the *applied* filters rather than whatever is currently in the dropdowns, so the file
   * always matches the totals the operator is looking at when they click it.
   */
  const handleExport = () => {
    const query = appliedQuery ?? buildQuery();

    if (!appliedQuery && !validateRanges()) {
      return;
    }

    setExporting(true);
    exportOverduePayments({
      sortBy,
      sortDirection: sortBy === 'dueDate' ? 'asc' : 'desc',
      query
    })
      .then((response) => {
        setExporting(false);

        if (isErrorInfo(response)) {
          birdToast.error('Export failed', 'Overdue payments');
          return;
        }

        const csvBlob =
          response.file instanceof Blob
            ? response.file
            : new Blob([response.file], { type: 'text/csv' });

        // The endpoint names the file; the date-stamped name is only the fallback.
        triggerBrowserDownload(
          csvBlob,
          `overdue-payments-${moment().format('YYYY-MM-DD')}.csv`,
          response.headers
        );
      })
      .catch(() => {
        setExporting(false);
        birdToast.error('Export failed', 'Overdue payments');
      });
  };

  const handlePageChange = (newPage: number) => {
    if (!appliedQuery) {
      return;
    }
    setOffset(newPage);
    loadPayments(newPage, limit, sortBy, appliedQuery);
  };

  const handleItemsPerPage = (itemsPerPage: number) => {
    if (!appliedQuery) {
      return;
    }
    setLimit(itemsPerPage);
    setOffset(1);
    loadPayments(1, itemsPerPage, sortBy, appliedQuery);
  };

  /**
   * A link to the reservation, prefixed with the app's base path.
   *
   * PUBLIC_URL matters here: the app is served under /admin/ and mounts at that basename, so a
   * plain '/reservations/edit/123' href in a new tab lands outside the app and 404s. The payments
   * list next door builds its reservation link the same way.
   */
  const reservationEditPath = (reservationId: number) =>
    `${process.env.PUBLIC_URL}${(
      APP_ROUTES.RESERVATION_MODULE.CHILDREN as RouteConfig
    ).EDIT.FULL_PATH.replace(':id', String(reservationId))}`;

  return (
    <div className="min-h-screen bg-creams-50 px-6 py-4">
      <section className="sticky top-0 z-30 -mx-6 mb-6 flex flex-col gap-4 border-b border-gray-200 bg-creams-50 px-6 py-4">
        <div>
          <h1 className="text-4xl font-recoleta font-semibold text-neutral-500">
            Overdue Payments
          </h1>
          <p className="mt-1 text-base font-gilroy-medium text-neutral-400">
            One row per payment that still owes money — overdue, and partially collected with a
            balance left — next to the reservation it was raised against. A partially collected
            payment is counted at its <span className="font-gilroy-bold">remaining</span> amount,
            not its full one. Every date range is{' '}
            <span className="font-gilroy-bold">inclusive</span> of both ends.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Payment Status
            </label>
            <Select
              isMulti
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={paymentStatusOptions}
              placeholder="Overdue & partially collected"
              value={paymentStatusOptions.filter((option) =>
                paymentStatuses.includes(option.value)
              )}
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, true>) => setPaymentStatuses(toIds(value))}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Reservation Status
            </label>
            <Select
              isMulti
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={reservationStatusOptions}
              placeholder="All but canceled & expired"
              value={reservationStatusOptions.filter((option) =>
                reservationStatuses.includes(option.value)
              )}
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, true>) =>
                setReservationStatuses(toIds(value))
              }
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">City</label>
            <Select
              isMulti
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={cityOptions}
              placeholder="All cities"
              value={cityOptions.filter((option) => cityIds.includes(option.value))}
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, true>) => {
                setCityIds(toIds(value));
                setAreaIds([]);
                setPropertyIds([]);
              }}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">Area</label>
            <Select
              isMulti
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={areaOptions}
              placeholder="All areas"
              value={areaOptions.filter((option) => areaIds.includes(option.value))}
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, true>) => {
                setAreaIds(toIds(value));
                setPropertyIds([]);
              }}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Project
            </label>
            <Select
              isMulti
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={propertyOptions}
              placeholder="All projects"
              value={propertyOptions.filter((option) => propertyIds.includes(option.value))}
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, true>) => setPropertyIds(toIds(value))}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Booked From
            </label>
            <ReactDatePicker
              selected={createdFrom}
              onChange={(date: Date | null) => setCreatedFrom(date)}
              placeholderText="Any"
              isClearable
              className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Booked To
            </label>
            <ReactDatePicker
              selected={createdTo}
              minDate={createdFrom ?? undefined}
              onChange={(date: Date | null) => setCreatedTo(date)}
              placeholderText="Any"
              isClearable
              className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Check-in From
            </label>
            <ReactDatePicker
              selected={checkInFrom}
              onChange={(date: Date | null) => setCheckInFrom(date)}
              placeholderText="Any"
              isClearable
              className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Check-in To
            </label>
            <ReactDatePicker
              selected={checkInTo}
              minDate={checkInFrom ?? undefined}
              onChange={(date: Date | null) => setCheckInTo(date)}
              placeholderText="Any"
              isClearable
              className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Check-out From
            </label>
            <ReactDatePicker
              selected={checkOutFrom}
              onChange={(date: Date | null) => setCheckOutFrom(date)}
              placeholderText="Any"
              isClearable
              className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Check-out To
            </label>
            <ReactDatePicker
              selected={checkOutTo}
              minDate={checkOutFrom ?? undefined}
              onChange={(date: Date | null) => setCheckOutTo(date)}
              placeholderText="Any"
              isClearable
              className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Due From
            </label>
            <ReactDatePicker
              selected={dueDateFrom}
              onChange={(date: Date | null) => setDueDateFrom(date)}
              placeholderText="Any"
              isClearable
              className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">Due To</label>
            <ReactDatePicker
              selected={dueDateTo}
              minDate={dueDateFrom ?? undefined}
              onChange={(date: Date | null) => setDueDateTo(date)}
              placeholderText="Any"
              isClearable
              className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Sort By
            </label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={SORT_OPTIONS}
              value={SORT_OPTIONS.find((option) => option.value === sortBy) ?? null}
              classNamePrefix="react-select"
              onChange={(
                value: ValueType<{ value: OverduePaymentsSortBy; label: string }, false>
              ) => handleSortChange(value?.value || 'dueDate')}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={handleGenerate}
            type={ButtonType.primary}
            size={ButtonSize.small}
            classNameButton="px-5 py-3 text-sm leading-4"
          >
            Generate
          </Button>
          <Button
            onClick={handleClearFilters}
            type={ButtonType.primaryInverted}
            size={ButtonSize.small}
            classNameButton="px-5 py-3 text-sm leading-4"
          >
            Clear filters
          </Button>
          <Button
            onClick={handleExport}
            type={ButtonType.primaryInverted}
            size={ButtonSize.small}
            disabled={!hasSearched || exporting}
            classNameButton="px-5 py-3 text-sm leading-4"
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
          {hasSearched && !loading && !error && (
            <span className="text-sm font-gilroy-medium text-neutral-400">
              {count} payment{count === 1 ? '' : 's'} across {totals.reservations} reservation
              {totals.reservations === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </section>

      <section>
        {loading && <LoadingData />}

        {!loading && error && (
          <div className="flex flex-col items-center py-10">
            <p className="text-base font-gilroy-medium text-neutral-400">
              The report could not be loaded.
            </p>
            <Button
              onClick={() => loadPayments(offset, limit, sortBy, appliedQuery ?? buildQuery())}
              type={ButtonType.primary}
              size={ButtonSize.small}
              className="mt-6"
              classNameButton="px-5 py-3 text-sm leading-4"
            >
              Retry
            </Button>
          </div>
        )}

        {!loading && !error && !hasSearched && (
          <p className="py-10 text-center text-base font-gilroy-medium text-neutral-400">
            Choose your filters and hit Generate. With none set, the report returns every payment
            that still owes money.
          </p>
        )}

        {!loading && !error && hasSearched && (
          <>
            {truncated && (
              <p className="mb-4 rounded-xl border border-warnings-200 bg-warnings-50 px-4 py-3 text-sm font-gilroy-medium text-warnings-700">
                This selection has more payments than one report can carry, so the totals below and
                any export are incomplete. Narrow the filters — by city, project or date — and run
                it again.
              </p>
            )}

            {/* Totals for the whole filtered set, not the page. EGP and USD are kept apart
                because they are not comparable; the combined figure converts each row at its own
                reservation's stored rate. */}
            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <p className="text-sm font-gilroy-medium text-neutral-400">Outstanding (EGP)</p>
                <p className="text-2xl font-gilroy-bold text-neutral-500">
                  {formatMoney(totals.remainingEgp)}
                </p>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <p className="text-sm font-gilroy-medium text-neutral-400">Outstanding (USD)</p>
                <p className="text-2xl font-gilroy-bold text-neutral-500">
                  {formatMoney(totals.remainingUsd)}
                </p>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                <p className="text-sm font-gilroy-medium text-neutral-400">
                  Outstanding (EGP equivalent)
                </p>
                <p className="text-2xl font-gilroy-bold text-neutral-500">
                  {formatMoney(totals.remainingEgpEquivalent)}
                </p>
              </div>
            </div>

            {rows.length === 0 ? (
              <NotFoundData />
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-md">
                <table className="w-full min-w-[2200px] text-left text-sm">
                  <thead>
                    <tr className="bg-creams-50">
                      {COLUMNS.map((column) => (
                        <th
                          key={column}
                          className="whitespace-nowrap px-3 py-3 font-gilroy-bold text-neutral-500"
                        >
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.paymentId} className="hover:bg-primary-50/40">
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {row.paymentId}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {PAYMENT_STATUS_LABELS[row.status] || titleCase(row.status)}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {formatDate(row.dueDate)}
                        </td>
                        <td
                          className={`border-t border-gray-100 px-3 py-3 text-right ${
                            row.daysOverdue > 0 ? 'font-gilroy-bold text-warnings-700' : ''
                          }`}
                        >
                          {row.daysOverdue > 0 ? row.daysOverdue : '—'}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 text-right whitespace-nowrap">
                          {formatMoney(row.totalAmount)} {(row.currency || '').toUpperCase()}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 text-right whitespace-nowrap">
                          {formatMoney(row.collectedAmount)}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 text-right whitespace-nowrap font-gilroy-bold">
                          {formatMoney(row.remainingAmount)} {(row.currency || '').toUpperCase()}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 text-right whitespace-nowrap">
                          {formatMoney(row.remainingEgpEquivalent)}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          <a
                            href={reservationEditPath(row.reservationId)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary-600 underline"
                          >
                            {row.referenceId || row.reservationId}
                          </a>
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {titleCase(row.reservationStatus) || '—'}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {formatDate(row.reservationCreatedAt)}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {formatDate(row.checkInDate)}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {formatDate(row.checkOutDate)}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {row.guestName || '—'}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {row.guestPhone || '—'}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {row.guestEmail || '—'}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {row.cityName || '—'}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {row.areaName || '—'}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {row.propertyName || '—'}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {row.unitName || row.roomName || '—'}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {titleCase(row.collector) || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {count > 0 && (
              <div className="flex items-center justify-center pb-4">
                <Pagination
                  itemCounts={count}
                  itemsPerPage={limit}
                  offset={offset}
                  handlePageChange={handlePageChange}
                  handleItemsPerPage={handleItemsPerPage}
                />
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default OverduePaymentsContainer;
